import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import DashboardIcon from "@mui/icons-material/Dashboard";
import UploadIcon from "@mui/icons-material/Upload";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MessageIcon from '@mui/icons-material/Message';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ImagingRequestIcon from './ImagingRequestIcon';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { Link, useLocation } from "react-router-dom";
import StudiesSubNavigation from "./StudiesSubNavigation";
import TeleconsultationSubNavigation from "./TeleconsultationSubNavigation";

const ensureArrayLength = (arr, len) => arr.concat(new Array(Math.max(len - arr.length, 0)).fill(null)).slice(0, len)

const SideNavBar = ({ handleDrawerToggle, mobileOpen, drawerWidth, access, hasSchedule, hasModalitySchedule }) => {

  const location = useLocation();

  const locationMatch = useMemo(() => ensureArrayLength(location.pathname.toLowerCase().substring(1).split('/'), 2), [location.pathname])

  const { org } = access

  const drawer = (
    <List>
      <ListItem key="Dashboard" disablePadding>
        <ListItemButton component={Link} to="/" selected={locationMatch[0] === ''}>
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
      </ListItem>
      {access.clinic && (
        <>
          <Divider />
          <ListItem key="Studies" disablePadding>
            <ListItemButton component={Link} to={`/clinic`} selected={locationMatch[1] === null && locationMatch[0] === 'clinic'}>
              <ListItemIcon>
                <LibraryBooksIcon />
              </ListItemIcon>
              <ListItemText primary="Studies" />
            </ListItemButton>
          </ListItem>
          <StudiesSubNavigation open={true} org={org} active={locationMatch[0] === 'clinic' && locationMatch[1]} hasSchedule={hasSchedule} />
        </>
      )}
      {access.consult && (
        <>
          <Divider />
          <ListItem key="Teleconsultation" disablePadding>
            <ListItemButton component={Link} to={`/consult`} selected={locationMatch[1] === null && locationMatch[0] === 'consult'}>
              <ListItemIcon>
                <MedicalInformationIcon />
              </ListItemIcon>
              <ListItemText primary="Teleconsultation" />
            </ListItemButton>
          </ListItem>
          <TeleconsultationSubNavigation open={true} org={org} active={locationMatch[0] === 'consult' && locationMatch[1]} />
        </>
      )}
      {hasSchedule && (
        <>
          <Divider />
          {access.clinic && (
            <ListItem key="Mobile Schedule" disablePadding>
              <ListItemButton component={Link} to="/schedule" selected={locationMatch[0] === 'schedule'}>
                <ListItemIcon>
                  <AccessTimeIcon />
                </ListItemIcon>
                <ListItemText primary="Mobile Schedule" />
              </ListItemButton>
            </ListItem>
          )}
        </>
      )}
      {hasModalitySchedule && access.clinic && (
        <>
          <ListItem key="Modality Schedule" disablePadding>
            <ListItemButton component={Link} to="/modality-schedule" selected={locationMatch[0] === 'modality-schedule'}>
              <ListItemIcon>
                <AccessTimeIcon />
              </ListItemIcon>
              <ListItemText primary="Modality Schedule" />
            </ListItemButton>
          </ListItem>
        </>
      )}
      {hasSchedule && (
        <>
          <ListItem key="Mobile Consult" disablePadding>
            <ListItemButton component={Link} to="/mobile-consult" selected={locationMatch[0] === 'mobile-consult'}>
              <ListItemIcon>
                <LocalShippingIcon />
              </ListItemIcon>
              <ListItemText primary="Mobile Consult" />
            </ListItemButton>
          </ListItem>
        </>
      )}
      {hasModalitySchedule && (
        <>
          <ListItem key="Request Imaging" disablePadding>
            <ListItemButton component={Link} to="/imaging-consult" selected={locationMatch[0] === 'imaging-consult'}>
              <ListItemIcon>
                <ImagingRequestIcon />
              </ListItemIcon>
              <ListItemText primary="Request Imaging" />
            </ListItemButton>
          </ListItem>
        </>
      )}
      {access?.clinic && (
        <>
          <Divider />
          <ListItem key="New Consult" disablePadding>
            <ListItemButton component={Link} to="/new-consult" selected={locationMatch[0] === 'new-consult'}>
              <ListItemIcon>
                <MessageIcon />
              </ListItemIcon>
              <ListItemText primary="Request Consult" />
            </ListItemButton>
          </ListItem>
          <ListItem key="New Study" disablePadding>
            <ListItemButton component={Link} to="/new" selected={locationMatch[0] === 'new'}>
              <ListItemIcon>
                <NoteAddIcon />
              </ListItemIcon>
              <ListItemText primary="Non-DICOM Study" />
            </ListItemButton>
          </ListItem>
          <ListItem key="Import Study" disablePadding>
            <ListItemButton component={Link} to="/import" selected={locationMatch[0] === 'import'}>
              <ListItemIcon>
                <UploadIcon />
              </ListItemIcon>
              <ListItemText primary="DICOM Study" />
            </ListItemButton>
          </ListItem>
        </>
      )}
    </List>
  )

  return (
    <>
      <CssBaseline />
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              position: "fixed",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          position="static"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              position: "static",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
};

export default SideNavBar;
